import { Link, useParams } from 'react-router-dom';
import type { PersonObservation } from '../api/types.js';
import { usePeople } from '../hooks/use-people.js';
import { Button } from '../components/button.js';
import { KIND } from '../components/people.js';
import { ALONE_HINT, mergedLabel, topDirs } from '../utils/activity-facts.js';

// A person's one-page sheet (ADR-030 section 6), for the operator to print or save as PDF for a one-to-one: what they
// did, what was noticed and accepted, what they are learning. Never the trust, the proposals or the other servers.
// Printed on paper whatever the theme, so the colours are fixed here.

const INK = '#1c1f2b';
const MUTED = '#5c6070';
const RULE = '#dcdee6';
const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 break-inside-avoid">
      <h2 className="mb-2 text-sm font-semibold" style={{ color: INK }}>{title}</h2>
      {children}
    </section>
  );
}

export function PersonSheet() {
  const { serverId = '', personId = '' } = useParams();
  const { data, error, isLoading } = usePeople(serverId);
  const person = data?.people.find((p) => p.id === personId);
  const back = `/servers/${serverId}/people`;

  if (isLoading) return <p className="p-6 text-sm" style={{ color: 'var(--color-text-3)' }}>Loading the sheet…</p>;
  if (error || !person) {
    return (
      <p role="alert" className="p-6 text-sm" style={{ color: 'var(--color-error)' }}>
        {error ? `Could not load it: ${(error as Error).message}` : 'This person is not on this server any more.'} <Link to={back} className="underline">Back to people</Link>
      </p>
    );
  }

  const repos = Object.entries(person.activity)
    .map(([repo, f]) => ({ repo, ...f, total: f.tickets + f.comments + f.prs + f.merged + (f.reviews ?? 0) }))
    .sort((a, b) => b.total - a.total || a.repo.localeCompare(b.repo));
  const byKind = (kind: PersonObservation['kind']) => (person.observations ?? []).filter((o) => o.kind === kind);
  const kinds = (Object.keys(KIND) as PersonObservation['kind'][]).filter((k) => byKind(k).length);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fff', color: INK }}>
      <style>{'@page { margin: 16mm } @media print { .sheet-tools { display: none } }'}</style>
      <div className="sheet-tools flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3" style={{ borderColor: RULE, backgroundColor: 'var(--color-surface)' }}>
        <Link to={back} className="text-sm" style={{ color: 'var(--color-text-2)' }}>Back to people</Link>
        <Button size="sm" onClick={() => window.print()}>Print or save as PDF</Button>
        <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Only what is below goes on the sheet: no trust, no proposals, nothing from other servers.</span>
      </div>

      <article className="mx-auto max-w-[42rem] px-6 py-10 print:p-0" data-testid="person-sheet">
        <h1 className="text-2xl font-semibold">{person.name}</h1>
        {(person.role || person.teams?.length) && <p className="mt-1 text-sm" style={{ color: MUTED }}>{[person.role, ...(person.teams ?? [])].filter(Boolean).join(', ')}</p>}

        {person.growthTarget && (
          <Section title="Learning">
            <p className="text-sm leading-relaxed">{person.growthTarget}</p>
          </Section>
        )}

        <Section title="Work per repository">
          {repos.length ? (
            <table className="w-full text-left text-sm">
              <thead style={{ color: MUTED }}>
                <tr className="text-xs">
                  <th className="py-1 pr-3 font-normal">Repository</th><th className="pr-3 text-right font-normal">PRs</th><th className="pr-3 text-right font-normal">Merged</th><th className="pr-3 text-right font-normal">Reviewed</th>
                  <th className="pr-3 text-right font-normal">Tickets</th><th className="pr-3 text-right font-normal">Comments</th><th className="font-normal">Last</th>
                </tr>
              </thead>
              <tbody>
                {repos.map((r) => (
                  <tr key={r.repo} className="border-t" style={{ borderColor: RULE }}>
                    <td className="py-1.5 pr-3 break-all">
                      {r.repo}
                      {r.dirs && <span className="block text-xs" style={{ color: MUTED }}>{topDirs(r)}</span>}
                    </td>
                    <td className="pr-3 text-right tabular-nums">{r.prs}</td><td className="pr-3 text-right tabular-nums whitespace-nowrap">{mergedLabel(r)}</td>
                    <td className="pr-3 text-right tabular-nums">{r.reviews ?? 0}</td>
                    <td className="pr-3 text-right tabular-nums">{r.tickets}</td><td className="pr-3 text-right tabular-nums">{r.comments}</td>
                    <td className="whitespace-nowrap" style={{ color: MUTED }}>{date(r.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm" style={{ color: MUTED }}>No activity seen yet.</p>}
          {repos.some((r) => r.alone !== undefined) && <p className="mt-2 text-xs" style={{ color: MUTED }}>{`${ALONE_HINT}. Under each repository, the directories they merged in most.`}</p>}
        </Section>

        {kinds.length > 0 && (
          <Section title="Observations">
            <dl className="space-y-3 text-sm">
              {kinds.map((k) => (
                <div key={k}>
                  <dt className="text-xs" style={{ color: MUTED }}>{KIND[k]}</dt>
                  {byKind(k).map((o) => <dd key={o.id} className="leading-relaxed">{o.text}</dd>)}
                </div>
              ))}
            </dl>
          </Section>
        )}

        <p className="mt-10 border-t pt-3 text-xs" style={{ borderColor: RULE, color: MUTED }}>
          {`As of ${date(new Date().toISOString())}, from the tickets, pull requests and comments seen on GitHub.`}
        </p>
      </article>
    </div>
  );
}
